import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const DAY_LABELS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

function firstValidationError(payload) {
  const errors = payload?.errors;
  if (!errors || typeof errors !== 'object') return null;
  const first = Object.values(errors)[0];
  if (Array.isArray(first) && first[0]) return String(first[0]);
  if (typeof first === 'string') return first;
  return null;
}

function todayIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function dayLabelFromIso(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return DAY_LABELS[d.getDay()] || '';
}

function createKasirStokis() {
  return {
    loading: false,
    loadingMovements: false,
    savingIn: false,
    savingOut: false,
    savingEdit: false,
    editOpen: false,
    items: [],
    movements: [],
    movementFilter: '',
    editForm: {
      id: '',
      name: '',
      current_stock: 0,
      unit: 'pcs',
      notes: '',
      is_active: true,
    },
    inForm: {
      name: '',
      quantity: 1,
      supplier_shop_name: '',
      moved_at: todayIso(),
      day_label: dayLabelFromIso(todayIso()),
      notes: '',
      unit: 'pcs',
    },
    outForm: {
      stokis_item_id: '',
      quantity: 1,
      moved_at: todayIso(),
      notes: '',
    },

    get selectedStock() {
      const it = this.items.find((i) => i.id === this.outForm.stokis_item_id);
      return it ? Number(it.current_stock) : null;
    },

    get selectedStockLabel() {
      if (this.selectedStock === null) return '— pilih barang —';
      const it = this.items.find((i) => i.id === this.outForm.stokis_item_id);
      return `${this.selectedStock} ${it?.unit || 'pcs'}`;
    },

    init() {
      try {
        const cfg = JSON.parse(this.$el.getAttribute('data-config') || '{}');
        this.items = Array.isArray(cfg.items) ? cfg.items : [];
        if (cfg.today) {
          this.inForm.moved_at = cfg.today;
          this.outForm.moved_at = cfg.today;
          this.inForm.day_label = cfg.day_label || dayLabelFromIso(cfg.today);
        }
      } catch (e) {
        console.error('kasirStokis config', e);
      }
      this.loadAll();
    },

    syncInDayLabel() {
      this.inForm.day_label = dayLabelFromIso(this.inForm.moved_at);
    },

    onOutItemChange() {
      if (this.selectedStock !== null && this.outForm.quantity > this.selectedStock) {
        this.outForm.quantity = this.selectedStock || 1;
      }
    },

    formatDate(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString('id-ID', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return iso;
      }
    },

    async loadAll() {
      await Promise.all([this.loadItems(), this.loadMovements()]);
    },

    async loadItems() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/stokis');
        this.items = data.data?.items || [];
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal memuat stok.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadMovements() {
      this.loadingMovements = true;
      try {
        const params = {};
        if (this.movementFilter) params.type = this.movementFilter;
        const { data } = await axios.get('/api/v1/kasir/stokis/movements', { params });
        this.movements = Array.isArray(data.data) ? data.data : [];
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal memuat riwayat.', 'error');
      } finally {
        this.loadingMovements = false;
      }
    },

    async submitIn() {
      if (this.savingIn) return;
      this.savingIn = true;
      try {
        const { data } = await axios.post('/api/v1/kasir/stokis/in', {
          name: this.inForm.name,
          quantity: Number(this.inForm.quantity) || 0,
          supplier_shop_name: this.inForm.supplier_shop_name,
          moved_at: this.inForm.moved_at,
          unit: this.inForm.unit || 'pcs',
          notes: this.inForm.notes || null,
        });
        window.toast?.(data.message || 'Stok masuk disimpan.', 'success');
        this.inForm.name = '';
        this.inForm.quantity = 1;
        this.inForm.supplier_shop_name = '';
        this.inForm.notes = '';
        await this.loadAll();
      } catch (e) {
        const payload = e?.response?.data;
        window.toast?.(
          firstValidationError(payload) || payload?.message || 'Gagal simpan barang masuk.',
          'error',
        );
      } finally {
        this.savingIn = false;
      }
    },

    async submitOut() {
      if (this.savingOut) return;
      if (!this.outForm.stokis_item_id) {
        window.toast?.('Pilih nama barang terlebih dahulu.', 'error');
        return;
      }
      this.savingOut = true;
      try {
        const { data } = await axios.post('/api/v1/kasir/stokis/out', {
          stokis_item_id: this.outForm.stokis_item_id,
          quantity: Number(this.outForm.quantity) || 0,
          moved_at: this.outForm.moved_at || null,
          notes: this.outForm.notes || null,
        });
        window.toast?.(data.message || 'Stok keluar disimpan.', 'success');
        this.outForm.quantity = 1;
        this.outForm.notes = '';
        await this.loadAll();
        this.onOutItemChange();
      } catch (e) {
        const payload = e?.response?.data;
        window.toast?.(
          firstValidationError(payload) || payload?.message || 'Gagal simpan barang keluar.',
          'error',
        );
      } finally {
        this.savingOut = false;
      }
    },

    openEdit(it) {
      this.editForm = {
        id: it.id,
        name: it.name || '',
        current_stock: Number(it.current_stock) || 0,
        unit: it.unit || 'pcs',
        notes: it.notes || '',
        is_active: it.is_active !== false,
      };
      this.editOpen = true;
    },

    closeEdit() {
      this.editOpen = false;
      this.savingEdit = false;
    },

    async submitEdit() {
      if (this.savingEdit || !this.editForm.id) return;
      this.savingEdit = true;
      try {
        const { data } = await axios.post(`/api/v1/kasir/stokis/items/${this.editForm.id}`, {
          name: this.editForm.name,
          current_stock: Number(this.editForm.current_stock) || 0,
          unit: this.editForm.unit || 'pcs',
          notes: this.editForm.notes || null,
          is_active: !!this.editForm.is_active,
        });
        window.toast?.(data.message || 'Data barang diperbarui.', 'success');
        this.closeEdit();
        await this.loadAll();
      } catch (e) {
        const payload = e?.response?.data;
        window.toast?.(
          firstValidationError(payload) || payload?.message || 'Gagal menyimpan perubahan.',
          'error',
        );
      } finally {
        this.savingEdit = false;
      }
    },
  };
}

Alpine.data('kasirStokis', createKasirStokis);
